'use client';

import { FC, MouseEvent, useEffect, useId, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { isFilled } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import classNames from 'classnames';

import { PAGE_ROUTES } from '@/constants';
import { useUiStoreSelector } from '@/data';
import { useIsMobile } from '@/hooks';
import type { PopoverAccountCtaSliceDefaultPrimary } from '@/types';
import { htmlSerializer, resolveCtaVariant } from '@/utils';
import { Button, Close, IconSvg, useAuth } from '@/components';
import type { ButtonColor } from '@/components/Button';

import headerStyles from './header.module.scss';
import styles from './HeaderSignInCta.module.scss';

type CtaFrequency = PopoverAccountCtaSliceDefaultPrimary['frequency'];
const CTA_TITLE = 'Sign In to Your Account';
const DEFAULT_CTA_TEXT = 'Sign in for personalized deals and easy reordering.';
const SHOWN_AT_KEY = '@sign_in_cta/shown_at';
const DAY_MS = 24 * 60 * 60 * 1000;
const DESKTOP_MIN_WIDTH = 1024;

const PRIORITY_OVERLAYS = [
  'is_cart_open',
  'is_search_open',
  'is_store_location_open',
  'is_nav_open',
  'is_megamenu_open',
  'is_special_bogo_modal_open',
  'is_store_validator_modal_open',
  'is_regulation_modal_open',
] as const;

let hasAutoOpened = false;

const TRIGGER_DELAY_MS: Record<string, number> = {
  Immediately: 0,
  'After 3 seconds': 3000,
  'After 5 seconds': 5000,
  'After 10 seconds': 10000,
};

const getShownStorage = (frequency: CtaFrequency) => {
  if (frequency === 'Once per day') return localStorage;
  if (frequency === 'Once per session') return sessionStorage;
  return null;
};

const wasAlreadyShown = (frequency: CtaFrequency): boolean => {
  try {
    const shownAt = Number(getShownStorage(frequency)?.getItem(SHOWN_AT_KEY));
    if (!shownAt) return false;
    return frequency === 'Once per session' || Date.now() - shownAt < DAY_MS;
  } catch {
    return false;
  }
};

const markAsShown = (frequency: CtaFrequency) => {
  try {
    getShownStorage(frequency)?.setItem(SHOWN_AT_KEY, String(Date.now()));
  } catch {
    // ignore quota / private mode
  }
};

interface HeaderSignInCtaProps {
  cta: PopoverAccountCtaSliceDefaultPrimary;
  showLabel?: boolean;
  label?: string;
}

const HeaderSignInCta: FC<HeaderSignInCtaProps> = ({
  cta,
  showLabel,
  label = 'Sign In',
}) => {
  const router = useRouter();
  const { loading } = useAuth();
  const overlays = useUiStoreSelector([...PRIORITY_OVERLAYS]);
  const { isMobile } = useIsMobile(DESKTOP_MIN_WIDTH);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<(_focusableElement?: HTMLElement) => void>(
    () => undefined,
  );
  const titleId = useId();

  const isCartOpen = overlays.is_cart_open;
  const isPriorityOverlayOpen = PRIORITY_OVERLAYS.some(key => overlays[key]);

  // Older Prismic repos still store `text` as Key Text (a string).
  const hasCtaText = Array.isArray(cta.text) && isFilled.richText(cta.text);
  const delayMs = TRIGGER_DELAY_MS[cta.display_trigger ?? ''] ?? 0;
  const frequency = cta.frequency ?? 'Once per session';
  const device = cta.device ?? 'All';
  const signInColor = resolveCtaVariant({
    ctaType: cta.sign_in_button_type,
    hasCustomColors: false,
    fallback: 'primary',
  }) as ButtonColor;
  const signUpColor = resolveCtaVariant({
    ctaType: cta.sign_up_button_type,
    hasCustomColors: false,
    fallback: 'secondary',
  }) as ButtonColor;
  const matchesDevice = device === 'All' || (device === 'Mobile') === isMobile;

  // Opening the cart dismisses the CTA (no reopen)
  useEffect(() => {
    if (!isCartOpen) return;
    const active = document.activeElement;
    closeRef.current(active instanceof HTMLElement ? active : undefined);
  }, [isCartOpen]);

  useEffect(() => {
    if (loading || isPriorityOverlayOpen || hasAutoOpened) return;

    const timer = setTimeout(() => {
      hasAutoOpened = true;
      if (!matchesDevice || wasAlreadyShown(frequency)) return;
      markAsShown(frequency);
      buttonRef.current?.click();
    }, delayMs);

    return () => clearTimeout(timer);
  }, [loading, isPriorityOverlayOpen, delayMs, matchesDevice, frequency]);

  const handleTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!e.isTrusted) return;
    e.preventDefault();
    if (loading) return;
    router.push(PAGE_ROUTES.SIGNIN);
  };

  const handleNavigate = (close: () => void, route: string) => {
    close();
    router.push(route);
  };

  const profileLabel = `Go to ${label} page`;

  return (
    <Popover className={styles.sign_in_cta}>
      {({ close }) => {
        closeRef.current = close;

        return (
          <>
            <PopoverButton
              aria-label={profileLabel}
              className={classNames(
                headerStyles.profile__action,
                styles.trigger,
              )}
              onClick={handleTriggerClick}
              ref={buttonRef}
            >
              <IconSvg name="profile" />
              {showLabel && (
                <span className={headerStyles.profile__action_label}>
                  {label}
                </span>
              )}
            </PopoverButton>

            <PopoverPanel aria-labelledby={titleId} className={styles.panel}>
              <div className={styles.body}>
                <div className={styles.peak} />
                <div className={styles.header}>
                  <p className={styles.title} id={titleId}>
                    {CTA_TITLE}
                  </p>
                  <Close className={styles.close} onClick={() => close()} />
                </div>
                <div className={styles.message}>
                  {hasCtaText ? (
                    <PrismicRichText
                      components={htmlSerializer}
                      field={cta.text}
                    />
                  ) : (
                    <p>{DEFAULT_CTA_TEXT}</p>
                  )}
                </div>
                <div className={styles.actions}>
                  <Button
                    className={styles.action}
                    color={signInColor}
                    onClick={() => handleNavigate(close, PAGE_ROUTES.SIGNIN)}
                    size="sm"
                  >
                    Sign in
                  </Button>
                  <Button
                    className={styles.action}
                    color={signUpColor}
                    onClick={() => handleNavigate(close, PAGE_ROUTES.SIGNUP)}
                    size="sm"
                  >
                    Create Account
                  </Button>
                </div>
              </div>
            </PopoverPanel>
          </>
        );
      }}
    </Popover>
  );
};

export { HeaderSignInCta };
